/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as ModalActionCreators from '@app/actions/ModalActionCreators';
import {modal} from '@app/actions/ModalActionCreators';
import {MicrophonePermissionDeniedModal} from '@app/components/alerts/MicrophonePermissionDeniedModal';
import {Logger} from '@app/lib/Logger';
import MediaPermissionStore from '@app/stores/MediaPermissionStore';
import {createDeepFilterProcessor} from '@app/utils/DeepFilterNoiseProcessor';
import {ensureNativePermission} from '@app/utils/NativePermissions';
import {isDesktop} from '@app/utils/NativeUtils';
import {getEffectiveBrowserNoiseSuppressionEnabled} from '@app/utils/VoiceNoiseSuppressionUtils';
import type {DeepFilterNoiseFilterProcessor} from 'deepfilternet3-noise-filter';
import {LocalAudioTrack} from 'livekit-client';
import {useCallback, useEffect, useRef, useState} from 'react';

const logger = new Logger('useMicTest');

export interface MicTestSettings {
	inputDeviceId: string;
	outputDeviceId: string;
	inputVolume: number;
	outputVolume: number;
	echoCancellation: boolean;
	noiseSuppression: boolean;
	autoGainControl: boolean;
	deepFilterNoiseSuppression: boolean;
	noiseSuppressionLevel: number;
}

export const useMicTest = (settings: MicTestSettings) => {
	const [isTesting, setIsTesting] = useState(false);
	const [level, setLevel] = useState(0);

	const audioContextRef = useRef<AudioContext | null>(null);
	const analyserRef = useRef<AnalyserNode | null>(null);
	const gainNodeRef = useRef<GainNode | null>(null);
	const destinationRef = useRef<MediaStreamAudioDestinationNode | null>(null);
	const audioElementRef = useRef<HTMLAudioElement | null>(null);
	const micStreamRef = useRef<MediaStream | null>(null);
	const animationFrameRef = useRef<number | null>(null);
	const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
	const timeDomainDataRef = useRef<Float32Array<ArrayBuffer> | null>(null);
	const isStartingRef = useRef(false);
	const deepFilterProcessorRef = useRef<DeepFilterNoiseFilterProcessor | null>(null);
	const localAudioTrackRef = useRef<LocalAudioTrack | null>(null);

	const micExplicitlyDenied = MediaPermissionStore.microphoneExplicitlyDenied;

	const updateLevel = useCallback(() => {
		if (!analyserRef.current || !timeDomainDataRef.current) {
			animationFrameRef.current = requestAnimationFrame(updateLevel);
			return;
		}

		analyserRef.current.getFloatTimeDomainData(timeDomainDataRef.current);

		let sumOfSquares = 0;
		for (let i = 0; i < timeDomainDataRef.current.length; i++) {
			const sample = timeDomainDataRef.current[i];
			sumOfSquares += sample * sample;
		}
		const rms = Math.sqrt(sumOfSquares / timeDomainDataRef.current.length);

		const MIN_DB = -60;
		const MAX_DB = 0;
		const db = 20 * Math.log10(Math.max(rms, 1e-10));
		const normalized = Math.max(0, Math.min(1, (db - MIN_DB) / (MAX_DB - MIN_DB)));

		setLevel(normalized);
		animationFrameRef.current = requestAnimationFrame(updateLevel);
	}, []);

	const stop = useCallback(() => {
		if (animationFrameRef.current) {
			cancelAnimationFrame(animationFrameRef.current);
			animationFrameRef.current = null;
		}

		if (audioElementRef.current) {
			audioElementRef.current.pause();
			audioElementRef.current.srcObject = null;
			audioElementRef.current = null;
		}

		if (sourceRef.current) {
			sourceRef.current.disconnect();
			sourceRef.current = null;
		}

		if (analyserRef.current) {
			analyserRef.current.disconnect();
			analyserRef.current = null;
		}

		if (gainNodeRef.current) {
			gainNodeRef.current.disconnect();
			gainNodeRef.current = null;
		}

		if (destinationRef.current) {
			destinationRef.current.disconnect();
			destinationRef.current = null;
		}

		if (deepFilterProcessorRef.current) {
			if (localAudioTrackRef.current) {
				void localAudioTrackRef.current.stopProcessor().catch((error) => {
					logger.warn('Failed to stop DeepFilterNet3 for mic test', error);
				});
			}
			deepFilterProcessorRef.current.destroy();
			deepFilterProcessorRef.current = null;
		}

		if (localAudioTrackRef.current) {
			localAudioTrackRef.current.stop();
			localAudioTrackRef.current = null;
		}

		if (micStreamRef.current) {
			micStreamRef.current.getTracks().forEach((track) => track.stop());
			micStreamRef.current = null;
		}

		if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
			void audioContextRef.current.close();
			audioContextRef.current = null;
		}

		timeDomainDataRef.current = null;

		setIsTesting(false);
		setLevel(0);
	}, []);

	const start = useCallback(async () => {
		if (isStartingRef.current) return;
		if (micExplicitlyDenied) {
			ModalActionCreators.push(modal(() => <MicrophonePermissionDeniedModal />));
			return;
		}

		isStartingRef.current = true;
		try {
			stop();
			if (isDesktop()) {
				const nativeResult = await ensureNativePermission('microphone');
				if (nativeResult === 'denied') {
					MediaPermissionStore.markMicrophoneExplicitlyDenied();
					ModalActionCreators.push(modal(() => <MicrophonePermissionDeniedModal />));
					return;
				}
			}

			const stream = await navigator.mediaDevices.getUserMedia({
				audio: {
					deviceId: settings.inputDeviceId !== 'default' ? {ideal: settings.inputDeviceId} : undefined,
					echoCancellation: settings.echoCancellation,
					noiseSuppression: getEffectiveBrowserNoiseSuppressionEnabled(settings),
					autoGainControl: settings.autoGainControl,
				},
			});

			micStreamRef.current = stream;

			let sourceTrack = stream.getAudioTracks()[0];

			if (settings.deepFilterNoiseSuppression && sourceTrack) {
				try {
					const localTrack = new LocalAudioTrack(sourceTrack, undefined, false);
					localAudioTrackRef.current = localTrack;

					const processor = createDeepFilterProcessor(settings.noiseSuppressionLevel);
					await localTrack.setProcessor(processor);
					deepFilterProcessorRef.current = processor;

					sourceTrack = localTrack.mediaStreamTrack;
					logger.info('Applied DeepFilterNet3 noise suppression for mic test');
				} catch (error) {
					logger.warn('Failed to apply DeepFilterNet3 for mic test, continuing without it', error);
					localAudioTrackRef.current = null;
					deepFilterProcessorRef.current = null;
				}
			}

			const audioContext = new AudioContext();
			audioContextRef.current = audioContext;

			if (audioContext.state === 'suspended') {
				await audioContext.resume();
			}

			const analyser = audioContext.createAnalyser();
			analyser.fftSize = 2048;
			analyserRef.current = analyser;
			timeDomainDataRef.current = new Float32Array(analyser.fftSize);

			const processedStream = new MediaStream([sourceTrack]);
			sourceRef.current = audioContext.createMediaStreamSource(processedStream);
			gainNodeRef.current = audioContext.createGain();
			gainNodeRef.current.gain.value = settings.inputVolume / 100;
			destinationRef.current = audioContext.createMediaStreamDestination();

			sourceRef.current.connect(analyser);
			analyser.connect(gainNodeRef.current);
			gainNodeRef.current.connect(destinationRef.current);

			audioElementRef.current = new Audio();
			audioElementRef.current.srcObject = destinationRef.current.stream;

			if (settings.outputDeviceId !== 'default' && 'setSinkId' in audioElementRef.current) {
				try {
					await (
						audioElementRef.current as HTMLAudioElement & {setSinkId: (deviceId: string) => Promise<void>}
					).setSinkId(settings.outputDeviceId);
				} catch (error) {
					logger.warn('Failed to set output device', error);
				}
			}

			audioElementRef.current.volume = settings.outputVolume / 100;
			await audioElementRef.current.play();

			setIsTesting(true);
			updateLevel();
		} catch (error) {
			logger.error('Error starting mic test', error);

			if (error instanceof Error && (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError')) {
				MediaPermissionStore.markMicrophoneExplicitlyDenied();
				ModalActionCreators.push(modal(() => <MicrophonePermissionDeniedModal />));
			}

			stop();
		} finally {
			isStartingRef.current = false;
		}
	}, [settings, updateLevel, stop, micExplicitlyDenied]);

	useEffect(() => {
		if (!isTesting) return;
		if (gainNodeRef.current) {
			gainNodeRef.current.gain.value = settings.inputVolume / 100;
		}
		if (audioElementRef.current) {
			audioElementRef.current.volume = settings.outputVolume / 100;
		}
	}, [isTesting, settings.inputVolume, settings.outputVolume]);

	useEffect(() => {
		return () => {
			stop();
		};
	}, [stop]);

	return {
		isTesting,
		level,
		start,
		stop,
	};
};
